// Tells an unverified user what is blocked, and offers a fresh link.
//
// Without this the first sign that anything is wrong is a 403 at the moment
// someone tries to pay — the worst possible time to learn about a step you
// missed. This says it up front, on the pages where they would start.
//
// Renders nothing once the address is confirmed, so it costs verified users
// nothing.

import { useState } from 'react';
import { MailWarning, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { auth } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

export function VerifyEmailNotice() {
  const { user } = useAuth();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (!user || user.email_verified_at) return null;

  const resend = async () => {
    setSending(true);
    try {
      await auth.resendVerification();
      setSent(true);
      toast.success('Confirmation email sent');
    } catch (e) {
      toast.error((e as Error).message || 'Could not send the email. Try again shortly.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div role="status" className="rounded-xl border border-warning-border bg-warning-bg p-3.5">
      <p className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-warning-fg">
        <MailWarning className="h-4 w-4" aria-hidden="true" />
        Confirm your email to send an order
      </p>
      <p className="mt-1 text-[12.5px] leading-relaxed text-warning-fg/90">
        We sent a link to <span className="font-semibold">{user.email}</span>. Receipts and delivery
        codes go to this address, so we confirm it before any money moves. You can browse in the
        meantime.
      </p>

      {sent ? (
        <p className="mt-2 text-[12px] font-medium text-success-fg">
          Sent. Check your inbox, and your spam folder.
        </p>
      ) : (
        <button
          type="button"
          onClick={resend}
          disabled={sending}
          className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg bg-warning-fg px-3 py-1.5 text-[12.5px] font-semibold text-white hover:opacity-90 disabled:opacity-60 transition-opacity"
        >
          {sending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
          Send a new link
        </button>
      )}
    </div>
  );
}
